import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { BookMeetingService } from '../../../../../core/services/book-meeting.service';
import { LocaleService } from '../../../../../core/services/locale.service';
import { formatCurrencyMinor } from '../../../../../core/utils/catering';
import { PaperIconComponent } from '@ricoh-paper/angular';
import { presenceSplit } from './confirmation-summary-logic';

/**
 * ConfirmationSectionComponent — shown after successful booking creation.
 *
 * Displays a meeting summary, catering order status, and a Done button.
 * If the primary room requires approval, shows a "Sent for Approval" state
 * instead of the standard "Meeting Booked" success state.
 */
@Component({
  selector: 'im-confirmation-section',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [PaperIconComponent, TranslocoPipe],
  template: `
    <div class="cs" data-testid="confirmation-section" [attr.data-booking-id]="service.lastResult()?.bookingId">
      @if (service.requiresApproval()) {
        <!-- Pending approval state -->
        <div class="cs__icon cs__icon--pending">
          <paper-icon name="clock" [size]="24" aria-hidden="true" />
          <span class="sr-only">{{ 'viewer.book_meeting_panel.confirmation.pending' | transloco }}</span>
        </div>
        <h3 class="cs__title">{{ 'viewer.book_meeting_panel.confirmation.sent_for_approval' | transloco }}</h3>
        <p class="cs__subtitle">
          {{ 'viewer.book_meeting_panel.confirmation.approval_subtitle' | transloco }}
        </p>
      } @else if (calendarFailed()) {
        <!-- Transient Graph failure — booking persisted but calendar didn't land -->
        <div class="cs__icon cs__icon--warning" data-testid="confirmation-failed">
          <paper-icon name="alert-triangle" [size]="24" aria-hidden="true" />
          <span class="sr-only">{{ 'viewer.book_meeting_panel.confirmation.warning' | transloco }}</span>
        </div>
        <h3 class="cs__title">{{ 'viewer.book_meeting_panel.confirmation.calendar_pending_title' | transloco }}</h3>
        <p class="cs__subtitle">
          {{ 'viewer.book_meeting_panel.confirmation.calendar_pending_subtitle' | transloco }}
        </p>
        @if (service.lastResult()?.calendarSyncError; as reason) {
          <p class="cs__failure-reason cs__failure-reason--inline">{{ reason }}</p>
        }
      } @else if (service.lastResult()?.pendingCalendar) {
        <!-- Awaiting Graph confirmation — room hasn't responded yet -->
        <div class="cs__icon cs__icon--pending" data-testid="confirmation-pending">
          <paper-icon name="clock" [size]="24" aria-hidden="true" />
          <span class="sr-only">{{ 'viewer.book_meeting_panel.confirmation.pending' | transloco }}</span>
        </div>
        <h3 class="cs__title">{{ 'viewer.book_meeting_panel.confirmation.booking_submitted' | transloco }}</h3>
        <p class="cs__subtitle">
          {{ 'viewer.book_meeting_panel.confirmation.booking_submitted_subtitle' | transloco }}
        </p>
      } @else {
        <!-- Success state -->
        <div class="cs__icon cs__icon--success" aria-live="polite">
          <paper-icon name="check-circle" [size]="24" aria-hidden="true" />
          <span class="sr-only">{{ 'viewer.book_meeting_panel.confirmation.success' | transloco }}</span>
        </div>
        <h3 class="cs__title">{{ 'viewer.book_meeting_panel.confirmation.meeting_booked' | transloco }}</h3>
        @if (service.lastResult()?.calendarSyncStatus === 'synced') {
          <p class="cs__subtitle cs__subtitle--ok">
            {{ 'viewer.book_meeting_panel.confirmation.confirmed_on_calendar' | transloco }}
            @if (latencyLabel(); as label) {
              <span class="cs__latency"> · {{ label }}</span>
            }
          </p>
        }
      }

      <!-- Meeting summary — design flow-confirmation SummaryRow cards:
           icon tile + uppercase label + detail lines per row. Bindings
           and branches are unchanged from the flat-row layout; only the
           visual treatment is new (Phase 10.B). -->
      <div class="cs__summary">
        <div class="cs__summary-row">
          <span class="cs__summary-icon" aria-hidden="true">
            <paper-icon name="calendar" [size]="14" />
          </span>
          <div class="cs__summary-body">
            <span class="cs__label">{{ 'viewer.book_meeting_panel.confirmation.meeting' | transloco }}</span>
            <span class="cs__value">{{ service.subject() || untitledLabel() }}</span>
          </div>
        </div>
        @if (service.rooms().length <= 1) {
          @if (service.primaryRoom(); as room) {
            <div class="cs__summary-row">
              <span class="cs__summary-icon" aria-hidden="true">
                <paper-icon name="map-pin" [size]="14" />
              </span>
              <div class="cs__summary-body">
                <span class="cs__label">{{ 'viewer.book_meeting_panel.confirmation.room' | transloco }}</span>
                <span class="cs__value">{{ room.name }}</span>
              </div>
            </div>
          }
        } @else {
          <!-- Multi-room: list every room with the primary flagged.
               All rooms share one Graph event (single iCalUId), so the
               calendar-sync status displayed in the header line above
               applies to every room equally — no per-row chip needed. -->
          <div class="cs__summary-row cs__summary-row--rooms" data-testid="confirmation-rooms">
            <span class="cs__summary-icon" aria-hidden="true">
              <paper-icon name="map-pin" [size]="14" />
            </span>
            <div class="cs__summary-body">
              <span class="cs__label">{{ roomsCountLabel(service.rooms().length) }}</span>
              <div class="cs__rooms-list">
                @for (room of service.rooms(); track room.spaceId) {
                  <div class="cs__room-line">
                    <span class="cs__value">{{ room.name }}</span>
                    @if (room.isPrimary) {
                      <span class="cs__room-tag">{{ 'viewer.book_meeting_panel.confirmation.role_primary' | transloco }}</span>
                    } @else if (room.roomRole === 'overflow') {
                      <span class="cs__room-tag cs__room-tag--overflow">{{ 'viewer.book_meeting_panel.confirmation.role_overflow' | transloco }}</span>
                    } @else if (room.roomRole === 'satellite') {
                      <span class="cs__room-tag cs__room-tag--satellite">{{ 'viewer.book_meeting_panel.confirmation.role_satellite' | transloco }}</span>
                    }
                  </div>
                }
              </div>
            </div>
          </div>
        }
        <div class="cs__summary-row">
          <span class="cs__summary-icon" aria-hidden="true">
            <paper-icon name="users" [size]="14" />
          </span>
          <div class="cs__summary-body">
            <span class="cs__label">{{ 'viewer.book_meeting_panel.confirmation.attendees' | transloco }}</span>
            <!-- "{N} attendees · X on-site, Y remote" when presence data
                 exists on the draft; plain "{N} people" otherwise. -->
            <span class="cs__value">{{ attendeeSummary() }}</span>
          </div>
        </div>
      </div>

      <!-- Join meeting — only shown when Microsoft Graph returned a join URL
           on the synchronous create. Avoids the user round-tripping to Outlook
           just to grab the link for a meeting they only just confirmed. -->
      @if (service.lastResult()?.onlineMeetingUrl; as joinUrl) {
        <a
          class="cs__join-link"
          [href]="joinUrl"
          target="_blank"
          rel="noopener">
          <paper-icon name="external-link" [size]="14" aria-hidden="true" />
          {{ 'viewer.book_meeting_panel.confirmation.join_teams_meeting' | transloco }}
        </a>
      }

      <!-- Catering orders -->
      @if (service.hasCatering()) {
        <div class="cs__section">
          <h4 class="cs__section-title">{{ 'viewer.book_meeting_panel.confirmation.catering' | transloco }}</h4>
          @for (order of service.cateringOrders(); track order.idempotencyKey) {
            @let failed = isFailedDraft(order.idempotencyKey);
            <div class="cs__catering-row">
              <span class="cs__vendor-name">{{ order.vendorName }}</span>
              <span class="cs__item-count">{{ itemsCountLabel(order.items.length) }}</span>
              <span class="cs__order-total">{{ fmtPrice(order.totalCostPence, order.currency) }}</span>
              @if (failed) {
                <span class="cs__status-badge cs__status-badge--failed">
                  <paper-icon name="alert-triangle" [size]="12" aria-hidden="true" />
                  <span class="sr-only">{{ 'viewer.book_meeting_panel.confirmation.warning_prefix' | transloco }}</span> {{ 'viewer.book_meeting_panel.confirmation.status.failed' | transloco }}
                </span>
              } @else if (service.requiresApproval()) {
                <span class="cs__status-badge cs__status-badge--pending">
                  <paper-icon name="clock" [size]="12" aria-hidden="true" />
                  <span class="sr-only">{{ 'viewer.book_meeting_panel.confirmation.pending_prefix' | transloco }}</span> {{ 'viewer.book_meeting_panel.confirmation.status.pending' | transloco }}
                </span>
              } @else {
                <span class="cs__status-badge cs__status-badge--confirmed">
                  <paper-icon name="check-circle" [size]="12" aria-hidden="true" />
                  <span class="sr-only">{{ 'viewer.book_meeting_panel.confirmation.confirmed_prefix' | transloco }}</span> {{ 'viewer.book_meeting_panel.confirmation.status.confirmed' | transloco }}
                </span>
              }
            </div>
            @if (failed && failureReasonFor(order.idempotencyKey); as reason) {
              <span class="cs__failure-reason">{{ reason }}</span>
            }
          }

          @if (hasFailedDrafts()) {
            <p class="cs__failure-note">
              {{ 'viewer.book_meeting_panel.confirmation.catering_failed_note' | transloco }}
            </p>
            @if (retryStatus(); as msg) {
              <p class="cs__retry-status">{{ msg }}</p>
            }
          }
        </div>
      }

      @if (service.lastResult()?.pendingCalendar) {
        <p class="cs__calendar-note">
          {{ 'viewer.book_meeting_panel.confirmation.waiting_for_calendar' | transloco }}
        </p>
      } @else if (calendarConfirmed()) {
        <p class="cs__calendar-note cs__calendar-note--ok">
          <paper-icon name="check-circle" [size]="12" aria-hidden="true" />
          {{ 'viewer.book_meeting_panel.confirmation.calendar_confirmed' | transloco }}
        </p>
      } @else if (calendarPollExhausted()) {
        <p class="cs__calendar-note cs__calendar-note--soft">
          {{ 'viewer.book_meeting_panel.confirmation.calendar_still_pending' | transloco }}
        </p>
      }

      <!-- Actions -->
      <div class="cs__actions">
        @if (hasFailedDrafts()) {
          <button
            type="button"
            class="cs__btn cs__btn--retry"
            [disabled]="retrying()"
            (click)="onRetryCatering()">
            @if (retrying()) {
              {{ 'viewer.book_meeting_panel.confirmation.retrying' | transloco }}
            } @else {
              <paper-icon name="refresh" [size]="13" /> {{ 'viewer.book_meeting_panel.confirmation.retry_catering' | transloco }}
            }
          </button>
        }
        <button class="cs__btn cs__btn--done" (click)="service.close()">{{ 'viewer.book_meeting_panel.confirmation.done' | transloco }}</button>
      </div>
    </div>
  `,
  styles: [`
    .cs {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 16px;
      padding: 24px 0 12px;
      text-align: center;
    }

    /* ── Icon ──────────────────────────────── */

    .cs__icon {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 56px; /* design flow-confirmation: large status roundel */
      height: 56px;
      border-radius: 50%;
    }

    .cs__icon--success {
      background: var(--success-bg);
    }

    .cs__icon--pending {
      background: var(--warning-bg);
    }

    .cs__icon--success paper-icon {
      color: var(--success);
    }

    .cs__icon--pending paper-icon {
      color: var(--warning);
    }

    .cs__icon--warning {
      background: var(--danger-bg, rgba(248, 113, 113, 0.12));
    }
    .cs__icon--warning paper-icon {
      color: var(--danger);
    }
    .cs__subtitle--ok {
      color: var(--success);
    }
    .cs__latency {
      color: var(--text-tertiary);
      font-variant-numeric: tabular-nums;
    }
    .cs__failure-reason--inline {
      margin-top: 4px;
      color: var(--danger);
      font-size: 12px;
    }
    .cs__join-link {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      align-self: stretch;
      justify-content: center;
      padding: 9px 12px;
      margin: 4px 16px 0;
      font-size: 13px;
      font-weight: 500;
      color: var(--text);
      text-decoration: none;
      background: var(--color-primary-tint, rgba(99, 102, 241, 0.12));
      border: 1px solid var(--color-primary, currentColor);
      border-radius: 8px;
      transition: filter 120ms ease;

      &:hover { filter: brightness(1.15); }
      &:focus-visible {
        outline: 2px solid var(--color-primary, currentColor);
        outline-offset: 2px;
      }
    }

    .sr-only {
      position: absolute;
      width: 1px;
      height: 1px;
      padding: 0;
      margin: -1px;
      overflow: hidden;
      clip: rect(0, 0, 0, 0);
      white-space: nowrap;
      border: 0;
    }

    /* ── Title / subtitle ─────────────────── */

    .cs__title {
      margin: 0;
      font-size: 20px; /* design flow-confirmation: headline scale */
      font-weight: 700;
      color: var(--text);
    }

    .cs__subtitle {
      margin: 0;
      max-width: 280px;
      font-size: 12px;
      line-height: 1.5;
      color: var(--text-secondary);
    }

    /* ── Summary ──────────────────────────── */

    /* Design SummaryRow cards: soft card per row with a tinted icon
       tile on the left, uppercase label + 13px detail lines right. */
    .cs__summary {
      width: 100%;
      display: flex;
      flex-direction: column;
      gap: 10px;
      padding: 4px 0;
      text-align: left;
    }

    .cs__summary-row {
      display: flex;
      align-items: flex-start;
      gap: 12px;
      padding: 12px;
      background: var(--surface-2);
      border: 1px solid var(--border-low);
      border-radius: 10px;
    }

    .cs__summary-icon {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 32px;
      height: 32px;
      flex: none;
      border-radius: 8px;
      background: var(--success-bg);
      color: var(--success);
    }

    .cs__summary-body {
      display: flex;
      flex-direction: column;
      gap: 4px;
      flex: 1;
      min-width: 0;
    }

    .cs__label {
      font-size: 11px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.08em;
      color: var(--text-tertiary);
    }

    .cs__value {
      font-size: 13px;
      font-weight: 500;
      color: var(--text);
    }

    /* Multi-room list — stack each room on its own line with a
       role tag chip, left-aligned under the label per the design. */
    .cs__rooms-list {
      display: flex;
      flex-direction: column;
      gap: 4px;
      align-items: flex-start;
    }
    .cs__room-line {
      display: inline-flex;
      align-items: center;
      gap: 6px;
    }
    .cs__room-tag {
      font-size: 10px; /* type-scale floor */
      font-weight: 700;
      letter-spacing: 0.04em;
      padding: 1px 6px;
      border-radius: var(--radius-pill);
      background: color-mix(in srgb, var(--success-strong) 14%, transparent);
      color: var(--success-strong);
      text-transform: uppercase;
    }
    .cs__room-tag--overflow {
      background: color-mix(in srgb, var(--warning-strong) 14%, transparent);
      color: var(--warning-strong);
    }
    .cs__room-tag--satellite {
      background: color-mix(in srgb, var(--info) 14%, transparent);
      color: var(--info);
    }

    /* ── Section ──────────────────────────── */

    .cs__section {
      width: 100%;
      text-align: left;
    }

    .cs__section-title {
      display: flex;
      align-items: center;
      gap: 8px;
      margin: 0 0 8px;
      font-size: 11px;
      font-weight: 600;
      color: var(--text-secondary);
      text-transform: uppercase;
      letter-spacing: 0.08em;

      /* Design SectionHeading: trailing hairline rule. */
      &::after {
        content: '';
        flex: 1;
        height: 1px;
        background: var(--border-low);
      }
    }

    /* ── Catering row ─────────────────────── */

    .cs__catering-row {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 6px 0;
    }

    .cs__vendor-name {
      font-size: 12px;
      font-weight: 500;
      color: var(--text);
    }

    .cs__item-count {
      font-size: 11px;
      color: var(--text-tertiary);
    }

    .cs__order-total {
      margin-left: auto;
      font-size: 12px;
      font-weight: 500;
      color: var(--text);
      font-variant-numeric: tabular-nums;
    }

    .cs__status-badge {
      display: inline-flex;
      align-items: center;
      gap: 3px;
      font-size: 10px; /* type-scale floor */
      font-weight: 600;
      padding: 2px 8px;
      border-radius: var(--radius-pill);
      white-space: nowrap;
    }

    .cs__status-badge--confirmed {
      color: var(--success);
      background: var(--success-bg);
    }

    .cs__status-badge--pending {
      color: var(--warning);
      background: var(--warning-bg);
    }

    .cs__status-badge--failed {
      color: var(--danger);
      background: var(--danger-bg, color-mix(in srgb, var(--danger) 15%, transparent));
    }

    .cs__failure-note {
      margin: 8px 0 0;
      font-size: 11px;
      line-height: 1.45;
      color: var(--text-secondary);
      padding: 8px 10px;
      border-radius: var(--radius-sm);
      background: color-mix(in srgb, var(--danger) 8%, transparent);
      border: 1px solid color-mix(in srgb, var(--danger) 25%, transparent);
    }

    .cs__retry-status {
      margin: 6px 0 0;
      font-size: 11px;
      color: var(--text-secondary);
    }

    .cs__calendar-note {
      width: 100%;
      margin: 8px 0 0;
      font-size: 11px;
      line-height: 1.45;
      color: var(--text-secondary);
      padding: 8px 10px;
      border-radius: var(--radius-sm);
      background: var(--warning-bg);
      border: 1px solid color-mix(in srgb, var(--warning) 35%, transparent);
      display: inline-flex;
      align-items: center;
      gap: 6px;
    }

    /* Confirmed — soft success accent. */
    .cs__calendar-note--ok {
      color: var(--success);
      background: var(--success-bg);
      border-color: color-mix(in srgb, var(--success) 35%, transparent);
    }

    /* Polling exhausted — softer than the active pending banner. */
    .cs__calendar-note--soft {
      color: var(--text-tertiary);
      background: var(--surface-2);
      border-color: var(--border-low);
    }

    /* Per-draft failure reason — sits directly under a failed catering row. */
    .cs__failure-reason {
      display: block;
      margin: -2px 0 4px;
      padding-left: 0;
      font-size: 10.5px;
      line-height: 1.4;
      color: var(--danger);
      opacity: 0.85;
    }

    /* ── Actions ──────────────────────────── */

    .cs__actions {
      width: 100%;
      padding-top: 8px;
      display: flex;
      flex-direction: column;
      gap: 6px;
    }

    .cs__btn {
      width: 100%;
      padding: 10px 16px;
      font-size: 13px;
      font-weight: 600;
      border: none;
      border-radius: var(--radius-sm);
      cursor: pointer;
      transition: opacity 0.15s ease, background 0.15s ease;
      font: inherit;
      font-size: 13px;
      font-weight: 600;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 6px;
    }

    .cs__btn--done {
      color: var(--text-on-accent);
      background: var(--color-primary);
      &:hover { opacity: 0.9; }
    }

    .cs__btn--retry {
      color: var(--text);
      background: var(--surface-2);
      border: 1px solid var(--border-low);
      &:hover:not(:disabled) { border-color: var(--border-med); background: var(--surface-3); }
      &:disabled { opacity: 0.5; cursor: not-allowed; }
    }

    @media (prefers-reduced-motion: reduce) {
      .cs__btn { transition: none; }
    }
  `],
})
export class ConfirmationSectionComponent implements OnInit, OnDestroy {
  protected readonly service = inject(BookMeetingService);
  private readonly transloco = inject(TranslocoService);
  private readonly locale = inject(LocaleService);

  /** True while a retry pass is in flight. */
  protected readonly retrying = signal(false);
  /** Last retry summary — keeps the user informed when some drafts still fail. */
  protected readonly retryStatus = signal<string | null>(null);

  protected readonly untitledLabel = computed(() => {
    this.locale.locale();
    return this.transloco.translate('viewer.book_meeting_panel.confirmation.untitled');
  });

  protected roomsCountLabel(count: number): string {
    this.locale.locale();
    return this.transloco.translate('viewer.book_meeting_panel.confirmation.n_rooms', { count });
  }

  protected peopleCountLabel(count: number): string {
    this.locale.locale();
    return this.transloco.translate('viewer.book_meeting_panel.confirmation.n_people', { count });
  }

  /**
   * People summary line — "{N} attendees · X on-site, Y remote" when the
   * draft's attendees carry presence data (the attendee chip sets
   * 'onsite' | 'remote'); plain "{N} people" otherwise. Presentation-only
   * derivation via the gated pure helper in confirmation-summary-logic.
   */
  protected readonly attendeeSummary = computed(() => {
    this.locale.locale();
    const attendees = this.service.attendees();
    const split = presenceSplit(attendees);
    if (!split.hasPresenceData) return this.peopleCountLabel(attendees.length);
    return this.transloco.translate(
      'viewer.book_meeting_panel.confirmation.attendee_split',
      { count: attendees.length, onsite: split.onsite, remote: split.remote },
    );
  });

  protected itemsCountLabel(count: number): string {
    this.locale.locale();
    return this.transloco.translate('viewer.book_meeting_panel.confirmation.n_items', { count });
  }

  /**
   * Set when the calendar poller observed a non-empty providerId — the
   * banner flips to a brief "Calendar confirmed" line. Distinct from
   * `pendingCalendar` going false on its own (which can happen if the
   * panel is opened on a booking that was never pending in the first
   * place — we don't want to flash a green banner in that case).
   */
  protected readonly calendarConfirmed = signal(false);
  /** Set when the poller hit the budget without confirmation. */
  protected readonly calendarPollExhausted = signal(false);

  /**
   * True when the synchronous Graph create came back with a transient
   * failure (status='failed' + an error message). Distinct from
   * `pendingCalendar` (which is hopeful) — this state means we KNOW the
   * calendar didn't accept and the user should be told why.
   */
  protected readonly calendarFailed = computed(() => {
    const r = this.service.lastResult();
    return r?.calendarSyncStatus === 'failed' && !!r?.calendarSyncError;
  });

  /**
   * Human-readable latency for the "Confirmed in 2.3s" affordance. Returns
   * null if the backend didn't include a latency (e.g. legacy queue path,
   * or non-Microsoft provider). The threshold is generous because
   * sub-second is uncommon and round numbers feel friendlier than "987ms".
   */
  protected readonly latencyLabel = computed<string | null>(() => {
    this.locale.locale();
    const ms = this.service.lastResult()?.calendarSyncLatencyMs;
    if (typeof ms !== 'number' || ms <= 0) return null;
    if (ms < 1000) return this.transloco.translate('viewer.book_meeting_panel.confirmation.confirmed_in_ms', { ms });
    const s = ms / 1000;
    return s < 10
      ? this.transloco.translate('viewer.book_meeting_panel.confirmation.confirmed_in_seconds', { seconds: s.toFixed(1) })
      : this.transloco.translate('viewer.book_meeting_panel.confirmation.confirmed_in_seconds', { seconds: Math.round(s) });
  });

  /** Aborts the in-flight calendar poll on destroy / panel close. */
  private pollAbort: AbortController | null = null;

  ngOnInit(): void {
    const result = this.service.lastResult();
    if (result?.pendingCalendar && result.bookingId) {
      this.startCalendarPoll(result.bookingId);
    }
  }

  ngOnDestroy(): void {
    this.pollAbort?.abort();
    this.pollAbort = null;
  }

  /**
   * Kick off the calendar poll. Fire-and-forget — the service mutates
   * `lastResult` directly when providerId comes through, so the OnPush
   * template re-renders via the signal read in the template.
   */
  private startCalendarPoll(bookingId: string): void {
    this.pollAbort?.abort();
    const controller = new AbortController();
    this.pollAbort = controller;

    this.service
      .pollCalendarUntilConfirmed(bookingId, controller.signal)
      .then(outcome => {
        if (controller.signal.aborted) return;
        if (outcome === 'confirmed') {
          this.calendarConfirmed.set(true);
        } else if (outcome === 'exhausted') {
          this.calendarPollExhausted.set(true);
        }
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        // Treat unexpected failure as exhaustion — the meeting is booked,
        // we just don't have calendar confirmation yet.
        this.calendarPollExhausted.set(true);
      });
  }

  protected fmtPrice(pence: number, currency?: string): string {
    return formatCurrencyMinor(pence, currency || 'GBP');
  }

  protected isFailedDraft(idempotencyKey: string): boolean {
    const failed = this.service.lastResult()?.failedDrafts;
    return !!failed?.some(d => d.idempotencyKey === idempotencyKey);
  }

  protected hasFailedDrafts(): boolean {
    return (this.service.lastResult()?.failedDrafts?.length ?? 0) > 0;
  }

  /**
   * Look up the captured failure reason for a specific catering draft.
   * Returns null when the draft isn't in the failed set, or when the
   * service couldn't extract a reason (in which case we fall back to the
   * generic "Failed" badge alone).
   */
  protected failureReasonFor(idempotencyKey: string): string | null {
    const f = this.service.lastResult()?.failedDrafts?.find(
      d => d.idempotencyKey === idempotencyKey,
    );
    // failureReason is added by the service post-mortem after a draft
    // mutation fails; it isn't on the base FailedDraft type yet.
    return (f as { failureReason?: string } | undefined)?.failureReason ?? null;
  }

  protected async onRetryCatering(): Promise<void> {
    this.retrying.set(true);
    this.retryStatus.set(null);
    try {
      const summary = await this.service.retryFailedCatering();
      if (summary.stillFailed === 0 && summary.succeeded > 0) {
        this.retryStatus.set(
          summary.succeeded === 1
            ? this.transloco.translate('viewer.book_meeting_panel.confirmation.retry_single_placed')
            : this.transloco.translate('viewer.book_meeting_panel.confirmation.retry_all_placed', { count: summary.succeeded }),
        );
      } else if (summary.succeeded > 0) {
        this.retryStatus.set(
          this.transloco.translate('viewer.book_meeting_panel.confirmation.retry_partial', {
            succeeded: summary.succeeded,
            failed: summary.stillFailed,
          }),
        );
      } else {
        this.retryStatus.set(
          this.transloco.translate('viewer.book_meeting_panel.confirmation.retry_unreachable'),
        );
      }
    } catch (err: unknown) {
      this.retryStatus.set((err instanceof Error ? err.message : null) ?? this.transloco.translate('viewer.book_meeting_panel.confirmation.retry_failed'));
    } finally {
      this.retrying.set(false);
    }
  }
}
